import { db } from "../firebase/firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { getAllScoreTypes } from "./scoreTypeService";
import { getAllSubjects } from "./subjectService";
import { callGeminiServer } from "./gemini";

const detectGradeLevel = (results: any[]): string | null => {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  const firstResult = results[0];

  const possibleGradeValues = [
    firstResult.grade,
    firstResult.gradeLevel,
    firstResult.classGrade,
    firstResult.studentGrade,
    firstResult.className,
    firstResult.class,
  ];

  for (const value of possibleGradeValues) {
    if (value === null || value === undefined) {
      continue;
    }

    const gradeMatch = String(value).match(/\b(10|11|12)\b/);

    if (gradeMatch) {
      return gradeMatch[1];
    }
  }

  return null;
};

const extractJsonFromResponse = (responseText: string): string => {
  const trimmedResponse = responseText.trim();

  const jsonCodeBlockMatch = trimmedResponse.match(
    /```(?:json)?\s*([\s\S]*?)\s*```/i
  );

  if (jsonCodeBlockMatch) {
    return jsonCodeBlockMatch[1].trim();
  }

  const firstBraceIndex = trimmedResponse.indexOf("{");
  const lastBraceIndex = trimmedResponse.lastIndexOf("}");

  if (
    firstBraceIndex !== -1 &&
    lastBraceIndex !== -1 &&
    lastBraceIndex > firstBraceIndex
  ) {
    return trimmedResponse.slice(firstBraceIndex, lastBraceIndex + 1);
  }

  throw new Error("Kết quả từ AI không chứa JSON hợp lệ.");
};

export const vireyaDashboardService = async (results: any[]) => {
  if (!Array.isArray(results) || results.length === 0) {
    throw new Error("Không có dữ liệu điểm để phân tích.");
  }

  const [subjects, scoreTypes] = await Promise.all([
    getAllSubjects(),
    getAllScoreTypes(),
  ]);

  const importantSubjectNames = [
    "Toán",
    "Ngữ Văn",
    "Tiếng Anh",
    "Tin Học",
  ];

  const subjectData: Record<string, Record<string, number>> = {};

  results.forEach((item) => {
    const subject = subjects.find(
      (subjectItem) => subjectItem.id === item.subjectId
    );

    const scoreType = scoreTypes.find(
      (scoreTypeItem) => scoreTypeItem.id === item.scoreTypeId
    );

    if (!subject || !scoreType) {
      return;
    }

    const score = Number(item.score);

    if (!Number.isFinite(score)) {
      return;
    }

    if (!subjectData[subject.name]) {
      subjectData[subject.name] = {};
    }

    subjectData[subject.name][scoreType.name] = score;
  });

  if (Object.keys(subjectData).length === 0) {
    throw new Error("Không tìm thấy môn học và điểm số hợp lệ.");
  }

  const gradeLevel = detectGradeLevel(results);

  const payload = {
    gradeLevel: gradeLevel ? `Lớp ${gradeLevel}` : null,
    importantSubjects: importantSubjectNames.filter(
      (subjectName) => subjectData[subjectName]
    ),
    subjects: subjectData,
  };

  const prompt = `
Bạn là chuyên gia phân tích kết quả học tập dành cho học sinh THPT.

Dưới đây là bảng điểm học kỳ I hoặc giữa học kỳ II của một học sinh.

Hãy căn cứ trực tiếp vào dữ liệu bảng điểm và khối lớp được cung cấp để thực hiện phân tích. Không được mặc định học sinh đang học lớp 10.

Nếu dữ liệu cho biết học sinh thuộc lớp 10, tiêu đề phải ghi lớp 10.
Nếu dữ liệu cho biết học sinh thuộc lớp 11, tiêu đề phải ghi lớp 11.
Nếu dữ liệu cho biết học sinh thuộc lớp 12, tiêu đề phải ghi lớp 12.
Nếu không xác định được khối lớp, không được tự suy đoán mà sử dụng cụm từ "Học sinh THPT".

Yêu cầu phân tích:

1. Phân tích tổng quan học lực hiện tại.

2. Nhận diện:
- Các môn học và năng lực nổi bật.
- Ưu điểm trong học tập.
- Các môn học, kỹ năng hoặc kiến thức cần cải thiện.
- Sự chênh lệch kết quả giữa các môn.

3. Gợi ý chiến lược học tập tổng thể phù hợp với:
- Khối lớp hiện tại.
- Giai đoạn giữa học kỳ.
- Năng lực thực tế của học sinh.
- Thời gian còn lại của học kỳ.

4. Đánh giá các môn học quan trọng:
- Mức điểm hiện tại.
- Điểm mạnh.
- Điểm yếu.
- Nội dung cần ưu tiên.
- Chiến lược cải thiện cụ thể.

5. Phân tích từng môn học theo các tiêu chí:
- Điểm số hiện tại.
- Xu hướng.
- Điểm mạnh.
- Điểm yếu.
- Chiến lược cải thiện.

Chỉ được nhận xét xu hướng tăng, giảm hoặc ổn định khi có từ hai mốc điểm trở lên và các mốc điểm có thể so sánh hợp lý.

Nếu chỉ có một mốc điểm hoặc không đủ dữ liệu để xác định xu hướng, trường "trend" phải ghi rõ:
"Chưa đủ dữ liệu để xác định xu hướng."

Không được tự tạo thêm điểm số, môn học hoặc dữ liệu không xuất hiện trong bảng điểm.

6. Tạo một tiêu đề ngắn gọn, phản ánh nhận định nổi bật nhất của bài phân tích.

Tiêu đề phải có khối lớp nếu xác định được, ví dụ:
- "Phân tích kết quả giữa học kỳ – Lớp 10: Nền tảng khá, cần củng cố các môn trọng tâm"
- "Phân tích kết quả giữa học kỳ – Lớp 11: Năng lực đồng đều, cần nâng cao tính ổn định"
- "Phân tích kết quả giữa học kỳ – Lớp 12: Tập trung củng cố các môn xét tuyển"

Dữ liệu bảng điểm:

${JSON.stringify(payload, null, 2)}

Chỉ trả về một đối tượng JSON hợp lệ.

Không sử dụng Markdown.
Không đặt JSON trong dấu ba dấu nháy ngược.
Không viết nội dung giải thích bên ngoài JSON.
Không sử dụng kiểu dữ liệu Timestamp của Firestore.
Không thêm chú thích vào JSON.

Cấu trúc JSON bắt buộc:

{
  "title": "string",
  "summary": "string",
  "importantSubjects": {
    "subjects": {
      "Tên môn học": {
        "Thường xuyên": null,
        "Giữa kỳ": null,
        "Cuối kỳ": null
      }
    },
    "overallStrengths": "string",
    "overallWeaknesses": "string",
    "learningAdvice": "string"
  },
  "subjectInsights": [
    {
      "subjectName": "string",
      "scores": {
        "Thường xuyên": null,
        "Giữa kỳ": null,
        "Cuối kỳ": null
      },
      "trend": "string",
      "strength": "string",
      "weakness": "string",
      "suggestion": "string"
    }
  ]
}

Với điểm số không có trong bảng điểm, sử dụng null, không tự điền điểm.
`;

  const responseText = await callGeminiServer(prompt);
  const jsonText = extractJsonFromResponse(responseText);

  try {
    return JSON.parse(jsonText);
  } catch (error) {
    console.error("Lỗi khi parse JSON từ Gemini:", error);
    console.error("Nội dung Gemini trả về:", responseText);
    throw new Error("Kết quả phân tích từ AI không đúng định dạng JSON.");
  }
};

export async function getVireyaDashboards(userId: string) {
  if (!userId?.trim()) {
    throw new Error("userId không hợp lệ.");
  }

  const dashboardsQuery = query(
    collection(db, "dashboards"),
    where("userId", "==", userId)
  );

  const querySnapshot = await getDocs(dashboardsQuery);

  return querySnapshot.docs.map((dashboardDocument) => ({
    id: dashboardDocument.id,
    ...dashboardDocument.data(),
  }));
}